import { crossedTarget, type Goal } from '~/data/nutrition'
import type { DayValue } from '~/lib/day-value'

/**
 * Whether a goal has been reached, and whether that has been said — see the nutrition spec,
 * Reaching it.
 *
 * It is said twice over, once for each reading that crosses: the scale weight of a weigh-in,
 * and the trend weight. On the scale it is said once for a goal and never again, whatever
 * later weigh-ins do — the scale wanders, and a goal congratulating somebody every time a
 * light morning dips under the target is a goal nobody believes. On the trend it is said again
 * where the trend has been away from the target since it was last said, because a trend that
 * went back over and came down again really did arrive twice.
 *
 * What has been said is remembered on the device, as the phone remembers it: it is about
 * whether this person has already seen the moment, which is not a fact about the account.
 */

const KEY = 'gymcloud.goal-reached'

/** Which reading crossed, where one did. */
export type Reading = 'trend' | 'scale'

interface Said {
  /** Whether the crossing has been said for this goal, per reading. */
  readonly scale?: boolean
  readonly trend?: boolean
}

function held(): Record<string, Said> {
  try {
    const raw = localStorage.getItem(KEY)
    return raw === null ? {} : (JSON.parse(raw) as Record<string, Said>)
  } catch {
    // A blocked or unreadable store says nothing has been said, which errs towards saying it.
    return {}
  }
}

function put(next: Record<string, Said>): void {
  try {
    localStorage.setItem(KEY, JSON.stringify(next))
  } catch {
    // Forgetting means it may be said twice, which is the kinder of the two failures.
  }
}

/** Said, and not said again until the rules below allow it. */
export function sayGoalReached(goalId: string, reading: Reading): void {
  const all = held()
  put({ ...all, [goalId]: { ...all[goalId], [reading]: true } })
}

/** Everything forgotten — what signing out does, the next account not being this one. */
export function forgetGoalsReached(): void {
  try {
    localStorage.removeItem(KEY)
  } catch {
    // Nothing to do about it, and nothing that depends on it having worked.
  }
}

/**
 * Which reading, if any, should say the goal has been reached now.
 *
 * Only the newest weigh-in can do it: this is the moment the weigh-in that did it is logged,
 * not a fact about the account that a page load could stumble on months later. Where both
 * readings cross at once the trend is the one said, so the trend is asked first.
 *
 * `scale` and `trend` are both oldest first, over every weigh-in, and `today` is only used to
 * keep a goal from being reached by a weigh-in made before it was set.
 */
export function goalReachedNow(
  goal: Goal | null,
  scale: readonly DayValue[],
  trend: readonly DayValue[],
): Reading | null {
  if (goal?.targetWeightKg == null || goal.aim === null) {
    return null
  }
  const newestScale = scale[scale.length - 1]
  const newestTrend = trend[trend.length - 1]
  if (newestScale === undefined || newestScale.day < goal.effectiveFrom) {
    return null
  }
  const said = held()[goal.id] ?? {}

  /*
   * The trend having been away since it was last said is what lets it be said again. It is
   * read off the trend as it stands: a trend back over the target has been away, so the next
   * arrival is a new one. Cleared here rather than when it happens, because nothing is
   * watching the trend between weigh-ins.
   */
  const trendCrossed =
    newestTrend?.day !== undefined &&
    newestTrend.day >= goal.effectiveFrom &&
    crossedTarget(goal, newestTrend.value)
  if (said.trend === true && !trendCrossed) {
    put({ ...held(), [goal.id]: { ...said, trend: false } })
    return null
  }
  if (trendCrossed && said.trend !== true) {
    return 'trend'
  }
  if (crossedTarget(goal, newestScale.value) && said.scale !== true) {
    return 'scale'
  }
  return null
}
